// src/pages/about/MajorActivities.jsx
import React from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Card, 
  CardContent,
  Grid,
  Chip,
  IconButton,
  Paper,
  Divider
} from '@mui/material';
import {
  AccountBalance as BankIcon,
  Business as BusinessIcon,
  School as EducationIcon,
  Psychology as IPRIcon,
  Groups as GroupsIcon,
  TrendingUp as SkillIcon,
  ArrowForward as ArrowIcon,
  Star as StarIcon,
  LocalHospital as LocalHospitalIcon,
  EnergySavingsLeaf as EcoIcon,
  Sports as SportsIcon,
  EmojiEvents as EmojiEventsIcon,
  TravelExplore as TravelExploreIcon
} from '@mui/icons-material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import SchoolIcon from '@mui/icons-material/School';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';

import CodeIcon from '@mui/icons-material/Code';

const activities = [
  {
    id: 1,
    title: "JanaNidhi Financial Services",
    description: "Comprehensive microfinancing solutions designed to empower communities through accessible financial instruments.",
    details: "Fixed deposit microfinancing, daily recurring deposits, and monthly recurring schemes tailored for financial inclusion.",
    icon: <BankIcon />,
    color: '#1976d2',
    gradient: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
    tags: ['Microfinance', 'Fixed Deposits', 'Recurring Schemes']
  },
  {
    id: 2,
    title: "Entrepreneurship Development",
    description: "Comprehensive programs to nurture and develop entrepreneurial mindset among aspiring business leaders.",
    details: "Structured seminars ranging from one-day workshops to intensive five-day programs, complemented by strategic industrial visits.",
    icon: <BusinessIcon />,
    color: '#512da8',
    gradient: 'linear-gradient(135deg, #512da8 0%, #4527a0 100%)',
    tags: ['Seminars', 'Industrial Visits', 'Business Development']
  },
  {
    id: 3,
    title: "Academic Project Guidance",
    description: "Expert mentorship and guidance for undergraduate and postgraduate students in their academic projects.",
    details: "Comprehensive support in project conceptualization, research methodology, and implementation for academic excellence.",
    icon: <EducationIcon />,
    color: '#2e7d32',
    gradient: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
    tags: ['UG Projects', 'PG Projects', 'Research Guidance']
  },
  {
    id: 4,
    title: "Intellectual Property Rights",
    description: "Specialized guidance on intellectual property protection and commercialization strategies.",
    details: "Comprehensive IPR consultation covering patents, trademarks, copyrights, and trade secrets for innovation protection.",
    icon: <IPRIcon />,
    color: '#ed6c02',
    gradient: 'linear-gradient(135deg, #ed6c02 0%, #e65100 100%)',
    tags: ['Patents', 'Innovation', 'Legal Protection']
  },
  {
    id: 5,
    title: "Learning with Earning",
    description: "Innovative student chapter programs that combine education with practical earning opportunities.",
    details: "Hands-on projects and initiatives that provide students with real-world experience while generating income.",
    icon: <GroupsIcon />,
    color: '#c2185b',
    gradient: 'linear-gradient(135deg, #c2185b 0%, #ad1457 100%)',
    tags: ['Student Programs', 'Practical Learning', 'Income Generation']
  },
  {
    id: 6,
    title: "Skill Development Programs",
    description: "Comprehensive training programs designed to enhance employability and professional competencies.",
    details: "Flexible online and offline training modules focusing on industry-relevant skills and emerging technologies.",
    icon: <SkillIcon />,
    color: '#00796b',
    gradient: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
    tags: ['Online Training', 'Offline Training', 'Employability']
  },
  {
  id: 7,
  title: "Software Development",
  description: "Empowering innovation through full-stack web, mobile, and UI/UX solutions.",
  details: "End-to-end development services including responsive web design, scalable mobile app development, and user-centric UI/UX design tailored for real-world applications.",
  icon: <CodeIcon />, // You can use @mui/icons-material/Code or any relevant icon
  color: '#3949ab',
  gradient: 'linear-gradient(135deg, #3949ab 0%, #1a237e 100%)',
  tags: ['Web Development', 'Mobile Apps', 'UI/UX Design', 'Software Solutions']
},
 {
    id: 8,
    title: "Quality Assessment & Third-Party Inspection (TPI)",
    description: "Independent quality assessment and third-party inspection services to ensure compliance and reliability.",
    details: "Evaluation of processes, products, and services through objective and standardized inspections across industries to ensure quality assurance and regulatory compliance.",
    icon: <FactCheckIcon />, // You can import this from @mui/icons-material
    color: '#455a64',
    gradient: 'linear-gradient(135deg, #455a64 0%, #263238 100%)',
    tags: ['TPI', 'Quality Assessment', 'Compliance', 'Inspection']
  },
  {
    id: 9,
    title: "Academia Support Programs",
    description: "Supportive initiatives aimed at strengthening educational institutions, students, teachers, and infrastructure.",
    details: "Holistic programs covering teacher training, student capacity building, infrastructure development, and academic-industry collaboration to enhance educational outcomes.",
    icon: <SchoolIcon />, // You can use @mui/icons-material/School
    color: '#6a1b9a',
    gradient: 'linear-gradient(135deg, #6a1b9a 0%, #4a148c 100%)',
    tags: ['Teachers', 'Students', 'Academic Infrastructure', 'Capacity Building']
  },
  {
    id: 10,
    title: "SME Efficiency Consulting",
    description: "Dedicated services for small and medium enterprises to boost labor productivity, machine performance, and product quality.",
    details: "Consulting and implementation support focused on workforce efficiency, machinery optimization, and improving overall product standards for SMEs.",
    icon: <PrecisionManufacturingIcon />, // You can use @mui/icons-material/PrecisionManufacturing
    color: '#ef6c00',
    gradient: 'linear-gradient(135deg, #ef6c00 0%, #e65100 100%)',
    tags: ['SMEs', 'Labor Productivity', 'Machine Efficiency', 'Product Quality']
  },
  {
    id: 11,
    title: "Jana Aushadhi",
    description: "Promotion and facilitation of Jan Aushadhi outlets for affordable generic medicines under the organization’s healthcare initiatives.",
    details: "Operating under NYCS’s domain of welfare, supporting access to quality healthcare through generic medicine stores.",
    icon: <LocalHospitalIcon />,
    color: '#388e3c',
    gradient: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
    tags: ['Healthcare', 'Generic Medicines', 'Public Welfare']
  },
  {
    id: 12,
    title: "Bio-Energy",
    description: "Initiatives promoting bio-energy solutions to empower rural economy and sustainable development.",
    details: "Projects in renewable energy—such as biogas or biomass—aimed at clean, community-centric power generation under NYCS’s environmental outreach.",
    icon: <EcoIcon />,
    color: '#2e7d32',
    gradient: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
    tags: ['Renewable Energy', 'Sustainability', 'Rural Development']
  },
  {
    id: 13,
    title: "GAIL Raftaar – Indian Speedstar",
    description: "Nationwide talent-scouting athletics programme for youth, in collaboration with GAIL, to nurture future Olympians.",
    details: "Pan-India track-event trials (100 m, 200 m, 800 m) for ages 11–17; it has engaged hundreds of thousands in trials, with world-class coaching and camps to groom elite athletes. :contentReference[oaicite:0]{index=0}",
    icon: <SportsIcon />,
    color: '#d32f2f',
    gradient: 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)',
    tags: ['Athletics', 'Talent Scouting', 'Sports Development']
  },
  {
    id: 14,
    title: "India Ka Khelotsav",
    description: "National sports festival initiative to foster sports culture and support aspiring athletes through NYCS platforms.",
    details: "A structured programme designed to create champions and a sustainable sporting ecosystem across youth communities. :contentReference[oaicite:1]{index=1}",
    icon: <EmojiEventsIcon />,
    color: '#fbc02d',
    gradient: 'linear-gradient(135deg, #fbc02d 0%, #f9a825 100%)',
    tags: ['Sports Festival', 'Athlete Support', 'Youth Engagement']
  },
  {
    id: 15,
    title: "Tours and Travels",
    description: "Organized travel and exposure programmes aimed at broadening youth horizons and experiential learning.",
    details: "Cooperative-driven tours for cultural exchange, exposure visits and educational travel—leveraging NYCS’s domain in tourism and youth development. :contentReference[oaicite:2]{index=2}",
    icon: <TravelExploreIcon />,
    color: '#0288d1',
    gradient: 'linear-gradient(135deg, #0288d1 0%, #0277bd 100%)',
    tags: ['Tourism', 'Experiential Learning', 'Youth Exposure']
  }

];

const MajorActivities = () => {
  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      py: 6
    }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #1976d2, #512da8)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2
            }}
          >
            Our Major Activities
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary" 
            sx={{ 
              maxWidth: 600, 
              mx: 'auto', 
              mb: 4,
              lineHeight: 1.6
            }}
          >
            Driving innovation, fostering entrepreneurship, and empowering communities through 
            comprehensive programs and strategic initiatives
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 4 }}>
            {[...Array(5)].map((_, i) => (
              <StarIcon key={i} sx={{ color: '#ffa726', fontSize: 30 }} />
            ))}
          </Box>
        </Box>

        {/* Statistics Section */}
        {/* <Paper 
          elevation={0}
          sx={{ 
            background: 'linear-gradient(135deg, #1976d2 0%, #512da8 100%)',
            color: 'white',
            p: 4,
            borderRadius: 4,
            mb: 6
          }}
        >
          <Grid container spacing={4} textAlign="center">
            <Grid item xs={12} sm={4}>
              <Typography variant="h3" fontWeight="bold">6+</Typography>
              <Typography variant="h6">Major Programs</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="h3" fontWeight="bold">1000+</Typography>
              <Typography variant="h6">Lives Impacted</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="h3" fontWeight="bold">50+</Typography>
              <Typography variant="h6">Success Stories</Typography>
            </Grid>
          </Grid>
        </Paper> */}

        {/* Activities Grid */}
        <Grid container spacing={4}>
          {activities.map((activity, index) => (
            <Grid item xs={12} md={6} key={activity.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 4,
                  transition: 'all 0.4s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px) scale(1.02)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
                  },
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                {/* Card Header with Icon */}
                <Box 
                  sx={{ 
                    background: activity.gradient,
                    color: 'white',
                    p: 3,
                    position: 'relative'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box 
                      sx={{ 
                        p: 1.5,
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.2)',
                        backdropFilter: 'blur(10px)'
                      }}
                    >
                      {React.cloneElement(activity.icon, { fontSize: 'large' })}
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight="bold">
                        {activity.title}
                      </Typography>
                      <Chip 
                        label={`Program ${activity.id}`}
                        size="small"
                        sx={{ 
                          background: 'rgba(255, 255, 255, 0.3)',
                          color: 'white',
                          mt: 1
                        }}
                      />
                    </Box>
                  </Box>
                  {/* Decorative Elements */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -20,
                      right: -20,
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: 'rgba(255, 255, 255, 0.1)',
                    }}
                  />
                </Box>

                <CardContent sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      mb: 2, 
                      fontWeight: 500,
                      color: '#2c3e50',
                      lineHeight: 1.6
                    }}
                  >
                    {activity.description}
                  </Typography>

                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ mb: 3, lineHeight: 1.5 }}
                  >
                    {activity.details}
                  </Typography>

                  {/* Tags */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                    {activity.tags.map((tag, tagIndex) => (
                      <Chip
                        key={tagIndex}
                        label={tag}
                        size="small"
                        sx={{
                          background: `${activity.color}15`,
                          color: activity.color,
                          border: `1px solid ${activity.color}30`,
                          fontWeight: 500
                        }}
                      />
                    ))}
                  </Box>

                  {/* Action Button */}
                  <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton 
                      sx={{ 
                        background: activity.gradient,
                        color: 'white',
                        '&:hover': {
                          background: activity.gradient,
                          transform: 'scale(1.1)',
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <ArrowIcon />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Call to Action Section */}
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Paper 
            elevation={0}
            sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              p: 6,
              borderRadius: 4
            }}
          >
            <Typography variant="h4" fontWeight="bold" sx={{ mb: 2 }}>
              Ready to Get Involved?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Join us in making a difference through our impactful programs and initiatives
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Chip 
                label="📞 Contact Us"
                sx={{ 
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  px: 2,
                  py: 1,
                  fontSize: '1rem'
                }}
              />
              <Chip 
                label="📧 Get Information"
                sx={{ 
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  px: 2,
                  py: 1,
                  fontSize: '1rem'
                }}
              />
              <Chip 
                label="🤝 Partner With Us"
                sx={{ 
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  px: 2,
                  py: 1,
                  fontSize: '1rem'
                }}
              />
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default MajorActivities;